import { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Modal,
  IconButton,
  Stack,
  TextField,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { tokens } from "../../../../../../theme";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { toJpeg } from "html-to-image";
import { toast } from "react-toastify";
import MemorialGraphic1080 from "../../../../../stream-management/components/share-message/ShareMessageThumbnail";
import oldMutualLogo from "../../../../../../assets/old-mutual.png";

type ShareLinkProps = {
  streamLink?: string;
  fullName: string;
  birthDate: string;
  deathDate: string;
  funeralDateText: string;
  venueText: string;
  imageUrl: string;
  patternSvgUrl: string;
  funeralStartTime?: string;
  livestreamedBy?: string;
  serviceType?: string;
  funeralHomeName?: string;
};

export default function ShareLink({
  streamLink,
  fullName,
  birthDate,
  deathDate,
  funeralDateText,
  venueText,
  imageUrl,
  patternSvgUrl,
  funeralStartTime,
  livestreamedBy,
  serviceType = "funeral",
  funeralHomeName,
}: ShareLinkProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [open, setOpen] = useState(false);
  const [downloadingGraphic, setDownloadingGraphic] = useState(false);
  const graphicRef = useRef<HTMLDivElement | null>(null);

  const safeStreamLink = useMemo(() => streamLink || "", [streamLink]);

  const isFuneral = serviceType.toLowerCase().includes("funeral")

  const isOldMutual = (funeralHomeName || "").toLowerCase().includes("old mutual");
  const providedBy = isOldMutual
    ? { name: "OLD MUTUAL", logo: oldMutualLogo }
    : undefined;

  const handleCopy = async () => {
    if (!safeStreamLink) {
      toast.error("No stream link available");
      return;
    }

    try {
      await navigator.clipboard.writeText(safeStreamLink);
      toast.success("Stream link copied");
    } catch {
      toast.error("Failed to copy stream link");
    }
  };

  const handleDownloadGraphic = async () => {
    if (!graphicRef.current) {
      toast.error("Graphic is not ready yet");
      return;
    }
    if (downloadingGraphic) return;

    setDownloadingGraphic(true);
    try {
      const dataUrl = await toJpeg(graphicRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        quality: 0.9,
        backgroundColor: "#ffffff",
      });

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `${fullName.replace(/\s+/g, "-").toLowerCase()}-memorial.jpg`;
      link.click();

      toast.success("Memorial graphic downloaded");
    } catch (error) {
      console.error(error);
      toast.error("Failed to download memorial graphic");
    } finally {
      setDownloadingGraphic(false);
    }
  };

  return (
    <>
      <Paper
        sx={{
          bgcolor: "#fff8f2",
          borderRadius: "12px",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          boxShadow: 0,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "18px",
              color: "#c59a70",
              mb: 0.5,
            }}
          >
            Stream ready! share stream link with client
          </Typography>

          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
              fontWeight: 500,
              fontSize: "12px",
              color: "#c59a70",
              lineHeight: 1.5,
            }}
          >
            To gather client feedback, it is important to create a thoughtful
            review request. Reach out after completing a project or service,
            thank them for their business, and invite them to share their
            experiences.
          </Typography>
        </Box>

        <Button
          onClick={() => setOpen(true)}
          sx={{
            bgcolor: "#c59a70",
            color: "white",
            borderRadius: "8px",
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "12px", 
            minWidth: 97,
            height: 45,
            "&:hover": {
              bgcolor: "#b08960",
            },
          }}
        >
          Share Link
        </Button>
      </Paper>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            width: { xs: "92%", sm: 520 },
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: colors.primary[900],
            borderRadius: "16px",
            mx: "auto",
            mt: { xs: 4, md: 6 },
            p: 3,
            position: "relative",
            boxShadow: "0 20px 60px rgba(0,0,0,0.16)",
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <CloseIcon />
          </IconButton>

          <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 2 }}>
            Share Stream
          </Typography>

          {isFuneral && (
          <Box sx={{ mb: 3 }}>
            <MemorialGraphic1080
              ref={graphicRef}
              fullName={fullName}
              birthDate={birthDate}
              deathDate={deathDate}
              funeralDateText={funeralDateText}
              venueText={venueText}
              imageUrl={imageUrl}
              patternSvgUrl={patternSvgUrl}
              funeralStartTime={funeralStartTime}
              livestreamedBy={livestreamedBy}
              providedBy={providedBy}
              previewSize={240}
            />
          </Box>
          )}

          <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>
            Stream link
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <TextField
              fullWidth
              size="small"
              value={safeStreamLink}
              InputProps={{ readOnly: true }}
            />

            <Button
              onClick={handleCopy}
              startIcon={<ContentCopyIcon />}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                minWidth: 110,
              }}
              variant="outlined"
            >
              Copy
            </Button>
          </Stack>

          {isFuneral && (
          <Button
            onClick={handleDownloadGraphic}
            disabled={downloadingGraphic}
            startIcon={
              downloadingGraphic ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <DownloadIcon />
              )
            }
            fullWidth
            sx={{
              mt: 2,
              textTransform: "none",
              borderRadius: "10px",
              py: 1.2,
              bgcolor: "#614438",
              color: "#fff",
              "&:hover": {
                bgcolor: "#704c3c",
              },
              "&.Mui-disabled": {
                bgcolor: "#614438",
                color: "#fff",
                opacity: 0.7,
              },
            }}
          >
            {downloadingGraphic ? "Preparing download..." : "Download Memorial Graphic"}
          </Button>
          )}
        </Box>
      </Modal>
    </>
  );
}